import {useEffect, useState} from 'react';

interface WindowSize {
    width: number;
    height: number;
}

class WindowSizeHelper {
    private size: WindowSize;
    private listeners: ((size: WindowSize) => void)[] = [];

    constructor() {
        this.size = {
            width: window.innerWidth,
            height: window.innerHeight,
        };

        // Add event listener to handle window resize
        window.addEventListener('resize', this.handleResize);
    }

    // Clean up the event listener when the component unmounts
    public cleanup() {
        window.removeEventListener('resize', this.handleResize);
    }

    public getSize(): WindowSize {
        return this.size;
    }

    public addListener(listener: (size: WindowSize) => void) {
        this.listeners.push(listener);
    }

    public removeListener(listener: (size: WindowSize) => void) {
        const index = this.listeners.indexOf(listener);
        if (index !== -1) {
            this.listeners.splice(index, 1);
        }
    }

    private handleResize = () => {
        this.size = {
            width: window.innerWidth,
            height: window.innerHeight,
        };

        // Notify all registered listeners
        this.listeners.forEach((listener) => {
            listener(this.size);
        });
    };
}

export function useWindowSize(): WindowSize {
    const [windowSize, setWindowSize] = useState<WindowSize>(() => {
        const helper = new WindowSizeHelper();
        helper.addListener((size) => setWindowSize(size)); // Use setWindowSize from the state
        return helper.getSize();
    });

    useEffect(() => {
        const helper = new WindowSizeHelper();
        helper.addListener((size) => {
            setWindowSize(size)
        }); // Use setWindowSize from the state

        return () => {
            helper.cleanup();
        };
    }, []);

    return windowSize;
}